/**
 * Queue for question-visit pings.
 *
 * Every time the student opens a question we record the moment as an offset
 * from `attempts.started_at` — the same t=0 as the recording chunks and the
 * proctor events. The admin's review screen uses these to jump the video
 * straight to when the student was looking at a given question.
 *
 * These are recorded, never derived. Estimating from cumulative `time_spent_ms`
 * falls apart the moment they jump around the palette, which is exactly what a
 * student does in the last ten minutes of a paper.
 *
 * Unlike answers, visits are append-only and order-independent, so they batch
 * happily and a duplicate is harmless (the server drops offsets within 250 ms
 * of one it already has).
 */
import type { RecordVisitInput } from "@exam/shared";
import { recordVisit } from "@/lib/exam-api";
import { isApiError } from "@/lib/api";

const FLUSH_DEBOUNCE_MS = 1_500;
const BACKOFF_MS = [2_000, 5_000, 15_000, 30_000];
const MAX_QUEUE = 500;

function storageKey(attemptId: string): string {
  return `ep:visits:pending:${attemptId}`;
}

export class VisitQueue {
  private readonly attemptId: string;
  private queue: RecordVisitInput[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private flushing = false;
  private stopped = false;
  private failures = 0;

  constructor(attemptId: string) {
    this.attemptId = attemptId;
    this.restore();
    if (this.queue.length > 0) this.schedule(FLUSH_DEBOUNCE_MS);
  }

  /**
   * @param questionId      the question just opened
   * @param offsetMs        ms since attempts.started_at, from the ServerClock
   * @param previousId      the question being left, if any
   * @param timeSpentMs     ms spent on `previousId`
   */
  record(
    questionId: string,
    offsetMs: number,
    previousId: string | null,
    timeSpentMs: number,
  ): void {
    if (this.stopped) return;

    this.queue.push({
      question_id: questionId,
      offset_ms: Math.max(0, Math.round(offsetMs)),
      time_spent_ms: Math.max(0, Math.round(timeSpentMs)),
      previous_question_id: previousId,
    });

    if (this.queue.length > MAX_QUEUE) {
      // Keep the newest. Visits are diagnostic; an unbounded array during a
      // long outage would be a worse problem than a truncated history.
      this.queue = this.queue.slice(-MAX_QUEUE);
    }

    this.persist();
    this.schedule(FLUSH_DEBOUNCE_MS);
  }

  async flush(): Promise<void> {
    if (this.flushing || this.queue.length === 0 || this.stopped) return;
    this.flushing = true;

    let failed = false;
    try {
      // Sent one at a time: the endpoint takes a single visit, and the volume
      // is a handful per minute at most.
      while (this.queue.length > 0) {
        const next = this.queue[0];
        if (!next) break;

        try {
          await recordVisit(this.attemptId, next);
          this.queue.shift();
          this.failures = 0;
        } catch (err) {
          if (isApiError(err, "conflict") || isApiError(err, "not_found")) {
            // The attempt is closed — these can never land. Drop and move on
            // rather than blocking the queue forever.
            this.queue.shift();
            continue;
          }
          failed = true;
          this.failures += 1;
          break;
        }
      }
    } finally {
      this.flushing = false;
      this.persist();
      if (!this.stopped && this.queue.length > 0) {
        this.schedule(
          failed
            ? (BACKOFF_MS[Math.min(this.failures - 1, BACKOFF_MS.length - 1)] ??
              30_000)
            : FLUSH_DEBOUNCE_MS,
        );
      }
    }
  }

  async finish(): Promise<void> {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    await this.flush();
    this.stopped = true;
  }

  dispose(): void {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private schedule(ms: number): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), ms);
  }

  private persist(): void {
    try {
      if (this.queue.length === 0) {
        localStorage.removeItem(storageKey(this.attemptId));
      } else {
        localStorage.setItem(
          storageKey(this.attemptId),
          JSON.stringify(this.queue),
        );
      }
    } catch {
      /* best effort */
    }
  }

  private restore(): void {
    try {
      const raw = localStorage.getItem(storageKey(this.attemptId));
      if (!raw) return;
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) this.queue = parsed as RecordVisitInput[];
    } catch {
      /* ignore corrupt state */
    }
  }
}
